import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import useTranslate from '../hooks/useTranslate';
import { FaAngleLeft, FaAngleRight, FaRegHeart } from 'react-icons/fa6'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import addToCart from '../helpers/addToCart'
import Context from '../context'
import { FaHeart, FaStar, FaPlus, FaMinus } from "react-icons/fa"
import SummaryApi from '../common'
import wishlistHelper from '../helpers/wishlistHelper'
import service from '../common/HttpService'
import displayINRCurrency from '../helpers/displayCurrency'
import { getCookie } from '../common/cookieHelper'

const HorizontalCardProduct = ({ category, brand, heading, limit = 6 }) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const loadingList = new Array(8).fill(null)
    const scrollElement = useRef()
    const { fetchUserAddToCart, cartItems, fetchUserWishlist, wishlistItems, translateText, languageCode } = useContext(Context)
    const user = useSelector(state => state?.user?.user)
    const navigate = useNavigate()

    const t = useTranslate({
        heading: heading || "",
        viewAll: "View All",
        off: "OFF",
        addToCart: "Add"
    });

    const [translatedData, setTranslatedData] = useState([])

    const isInWishlist = (productId) => {
        return (wishlistItems || []).some(item => item._id === productId)
    }

    const handleAddToCart = async (e, product) => {
        e?.preventDefault();
        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id');
        if (!userId) {
            navigate("/login");
            return;
        }
        await addToCart(e, product, userId)
        fetchUserAddToCart()
    }

    const handleWishlist = (e, product) => {
        e?.preventDefault();
        e?.stopPropagation();
        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id');
        if (!userId) {
            navigate("/login");
            return;
        }
        wishlistHelper.addToWishlist(e, product)
        fetchUserWishlist()
    }

    const updateQty = async (e, id, qty, change) => {
        e?.stopPropagation()
        e?.preventDefault()

        const newQty = qty + change

        if (newQty <= 0) {
            const response = await service.post(SummaryApi.delete_cart.url, {
                id: id,
                user_id: user?._id || user?.id
            })
            if (response.success === "true" || response.success === true) {
                fetchUserAddToCart()
            }
        } else {
            const response = await service.post(SummaryApi.update_cart.url, {
                id: id,
                qty: newQty,
                user_id: user?._id || user?.id
            })
            if (response.success === "true" || response.success === true) {
                fetchUserAddToCart()
            }
        }
    }

    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            // Using page_no and lim as per updated API requirement
            const response = await service.post(SummaryApi.pagination.url, {
                page_no: 1,
                lim: limit
            })

            const backendBaseUrl = (SummaryApi.allProduct.url || "").split('/Api')[0]

            if (response.success === "true" || response.success === true) {
                let fetchedProducts = []
                if (Array.isArray(response.extraData)) {
                    const paginatedItem = response.extraData.find(item => item && item.pagination_data)
                    fetchedProducts = paginatedItem ? paginatedItem.pagination_data : []
                } else if (response.data) {
                    fetchedProducts = response.data
                }

                const mappedProducts = fetchedProducts.map(prod => {
                    let images = [];
                    const itemImages = prod.image || prod.productImage || [];
                    if (Array.isArray(itemImages)) {
                        images = itemImages;
                    } else if (typeof itemImages === 'string' && itemImages) {
                        images = itemImages.includes(',') ? itemImages.split(',') : [itemImages];
                    }

                    const processedImages = images.map(img => {
                        if (typeof img === 'string' && img && !img.startsWith('http')) {
                            const cleanPath = img.startsWith('/') ? img.substring(1) : img;
                            return `${backendBaseUrl}/${cleanPath}`;
                        }
                        return img;
                    }).filter(Boolean);

                    if (processedImages.length === 0) {
                        processedImages.push('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ2sSeQqjaUTuZ3gRgkKjidpaipF_l6s72lBw&s');
                    }

                    return {
                        ...prod,
                        _id: prod.id || prod._id,
                        productName: prod.itemname || prod.productName,
                        name: prod.vendor_name || prod.brandName || prod.name || "FasalShop",
                        category: prod.category_name || prod.category || "",
                        productImage: processedImages,
                        price: Number(prod.item_rates?.[0]?.mrp) || Number(prod.rates?.[0]?.mrp) || Number(prod.price) || 0,
                        sellingPrice: Number(prod.item_rates?.[0]?.salerate) || Number(prod.rates?.[0]?.salerate) || Number(prod.sellingPrice) || 0,
                        rates: prod.item_rates || prod.rates || [],
                        brandName: prod.vendor_name || prod.brandName || "FasalShop"
                    }
                })

                let filtered = mappedProducts
                if (category) {
                    filtered = mappedProducts.filter(item => {
                        const itemCat = (item.category || "").toLowerCase().trim();
                        const targetCat = category.toLowerCase().trim();
                        return itemCat === targetCat || itemCat.includes(targetCat) || targetCat.includes(itemCat);
                    })
                }

                setData(filtered)
            }
        } catch (error) {
            console.error("Error in HorizontalCardProduct fetchData:", error)
        } finally {
            setLoading(false)
        }
    }, [category, limit])

    useEffect(() => {
        fetchData()
    }, [fetchData]);

    useEffect(() => {
        const translateProducts = async () => {
            if (!languageCode || languageCode === 'en' || !data.length) {
                setTranslatedData(data);
                return;
            }
            try {
                const translated = await Promise.all(
                    data.map(async (prod) => ({
                        ...prod,
                        productName: await translateText(prod.productName, languageCode),
                        category: await translateText(prod.category, languageCode)
                    }))
                );
                setTranslatedData(translated);
            } catch (error) {
                console.error("HorizontalCardProduct translation error:", error);
                setTranslatedData(data);
            }
        };
        translateProducts();
    }, [languageCode, translateText, data]);

    const scrollRight = () => {
        scrollElement.current.scrollLeft += 300
    }
    const scrollLeft = () => {
        scrollElement.current.scrollLeft -= 300
    }

    const getCartQty = (productId) => {
        const item = cartItems.find(el => el.product_id === productId || el._id === productId)
        return item ? (Number(item.qty) || Number(item.quantity) || 1) : 0
    }

    const getCartId = (productId) => {
        const item = cartItems.find(el => el.product_id === productId || el._id === productId)
        if (!item) return null
        return (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));
    }

    return (
        <div className='container mx-auto px-4 my-6 relative'>
            <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl md:text-2xl font-black text-slate-800 uppercase tracking-tight'>{t.heading}</h2>
                <Link to={`/product-category?category=${encodeURIComponent(category || heading || "")}`} className='text-[#00b259] hover:text-[#143d2c] font-black text-[11px] uppercase tracking-widest transition-all'>{t.viewAll}</Link>
            </div>

            <div className='relative'>
                <button className='bg-white shadow-md rounded-full p-2 absolute -left-4 top-[40%] z-10 hidden md:block hover:bg-slate-100 transition-all active:scale-90' onClick={scrollLeft}><FaAngleLeft className='text-slate-800' /></button>
                <button className='bg-white shadow-md rounded-full p-2 absolute -right-4 top-[40%] z-10 hidden md:block hover:bg-slate-100 transition-all active:scale-90' onClick={scrollRight}><FaAngleRight className='text-slate-800' /></button>

                <div className='flex items-center gap-4 md:gap-6 overflow-x-scroll scrollbar-none transition-all scroll-smooth pb-4 px-2' ref={scrollElement}>
                    {
                        loading ? (
                            loadingList.map((_, index) => (
                                <div key={index} className='w-full min-w-[280px] md:min-w-[320px] max-w-[280px] md:max-w-[320px] h-36 bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-50 flex'>
                                    <div className='shimmer-effect h-full w-28'></div>
                                    <div className='p-4 flex-1 space-y-3'>
                                        <div className='h-4 shimmer-effect rounded w-3/4'></div>
                                        <div className='h-3 shimmer-effect rounded w-1/2'></div>
                                        <div className='h-5 shimmer-effect rounded w-1/3 mt-2'></div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            translatedData.map((product, index) => {
                                const qty = getCartQty(product._id)
                                const cartId = getCartId(product._id)

                                return (
                                    <Link key={index} to={"/product/" + product?._id} className='w-full min-w-[280px] md:min-w-[320px] max-w-[280px] md:max-w-[320px] h-36 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group border border-slate-100 overflow-hidden flex'>
                                        <div className='w-28 h-full bg-[#f8fcf9] p-3 flex items-center justify-center relative overflow-hidden shrink-0'>
                                            <img src={product.productImage[0]} alt={product.productName} className='h-full w-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-500' />
                                            {
                                                product.price > product.sellingPrice && (
                                                    <div className='absolute top-2 left-2 bg-[#00b259] text-white text-[8px] font-black px-1.5 py-0.5 rounded shadow-sm'>
                                                        {Math.round(((product.price - product.sellingPrice) / product.price) * 100)}% {t.off}
                                                    </div>
                                                )
                                            }
                                            <button
                                                className={`absolute top-2 right-2 p-1.5 rounded-full shadow-sm transition-all duration-300 bg-white hover:bg-slate-50 border border-slate-100 z-10 ${isInWishlist(product._id) ? 'text-red-500' : 'text-slate-400 hover:text-red-500'}`}
                                                onClick={(e) => handleWishlist(e, product)}
                                            >
                                                {isInWishlist(product._id) ? <FaHeart size={10} /> : <FaRegHeart size={10} />}
                                            </button>
                                        </div>

                                        <div className='p-4 flex-1 flex flex-col justify-between'>
                                            <div>
                                                <p className='text-[8px] font-black uppercase text-[#00b259] tracking-widest mb-0.5'>{product.category}</p>
                                                <h3 className='text-slate-800 font-bold text-sm line-clamp-1 group-hover:text-[#00b259] transition-colors'>{product.productName}</h3>
                                                <div className='flex items-center gap-2 mt-1'>
                                                    <span className='text-[#143d2c] font-black text-sm'>{displayINRCurrency(product.sellingPrice)}</span>
                                                    {product.price > product.sellingPrice && (
                                                        <span className='text-slate-400 line-through text-[10px] font-medium'>{displayINRCurrency(product.price)}</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className='flex items-center justify-end'>
                                                {
                                                    qty > 0 ? (
                                                        <div className='flex items-center gap-3 bg-[#f8fcf9] rounded-lg border border-[#eef5ef] p-1 scale-90 origin-right'>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    updateQty(e, cartId, qty, -1);
                                                                }}
                                                                className='p-1 rounded bg-white text-[#143d2c] hover:bg-red-50 hover:text-red-500 transition-colors shadow-sm'
                                                            >
                                                                <FaMinus size={8} />
                                                            </button>
                                                            <span className='text-xs font-black text-[#143d2c]'>{qty}</span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    updateQty(e, cartId, qty, 1);
                                                                }}
                                                                className='p-1 rounded bg-white text-[#143d2c] hover:bg-[#00b259] hover:text-white transition-colors shadow-sm'
                                                            >
                                                                <FaPlus size={8} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className='relative overflow-hidden w-24 md:w-28 h-8 md:h-9 bg-white border border-[#00b259] text-[#00b259] rounded-lg uppercase font-black tracking-[0.4px] leading-none flex items-center justify-center select-none group'
                                                            onClick={(e) => handleAddToCart(e, product)}
                                                        >
                                                            <div className='absolute -bottom-[50%] left-0 w-full h-full bg-[#143d2c] origin-bottom transition-transform duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] -skew-y-[9.3deg] scale-y-0 group-hover:scale-y-[2] z-0'></div>
                                                            <span className='relative z-10 transition-all duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] group-hover:text-white flex items-center gap-1.5 text-[9px] md:text-[10px] group-hover:-translate-y-6 group-hover:opacity-0'>
                                                                <FaPlus size={8} /> {t.addToCart}
                                                            </span>
                                                            <span className='absolute top-[50%] left-[50%] -translate-x-[50%] translate-y-[150%] opacity-0 z-20 text-white transition-all duration-[600ms] ease-[cubic-bezier(0.48,0,0.12,1)] group-hover:-translate-y-[50%] group-hover:opacity-100 flex items-center justify-center gap-1.5 text-[9px] md:text-[10px] w-full'>
                                                                <FaPlus size={8} /> {t.addToCart}
                                                            </span>
                                                        </button>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </Link>
                                )
                            })
                        )
                    }
                </div>
            </div>
        </div>
    )
}

export default HorizontalCardProduct